<template>
  <ClientOnly>
    <div v-if="shouldShowOrderBlock" class="order-widget-container">
      <swiper-container
        v-if="swiperKey > 0"
        :key="`swiper-${swiperKey}`"
        ref="swiperContainerRef"
        class="order-widget-swiper"
        :class="swiperClasses"
        :loop="swiperConfig.loop"
        :slides-per-view="swiperConfig.slidesPerView"
        :space-between="swiperConfig.spaceBetween"
        :allow-touch-move="swiperConfig.allowTouchMove"
        :allow-slide-next="swiperConfig.allowSlideNext"
        :allow-slide-prev="swiperConfig.allowSlidePrev"
        :touch-ratio="swiperConfig.touchRatio"
        :touch-angle="swiperConfig.touchAngle"
        :grab-cursor="swiperConfig.grabCursor"
        :resistance="swiperConfig.resistance"
        :resistance-ratio="swiperConfig.resistanceRatio"
        :breakpoints="swiperBreakpoints"
        @swiperslidechange="onSlideChange"
      >
        <swiper-slide
          v-for="orderSummary in navigationOrders"
          :key="String(orderSummary.id)"
          class="order-widget-slide"
        >
          <div class="order-widget" @click="handleOrderClick(orderSummary)">
            <div class="order-widget__top">
              <div class="order-widget__meta w-full">
                <div class="order-widget__badge" :class="getBadgeClass(orderSummary)">
                  <div class="order-widget__badge-dot" />
                  <span class="order-widget__badge-text">
                    {{ getMessage(orderSummary) }}
                  </span>
                </div>
                <div class="order-widget__order-id flex justify-between w-full items-center">
                  Заказа #{{ formatOrderId(orderSummary) }}

                  <div
                    v-if="shouldShowTimer(orderSummary)"
                    class="order-widget__timer-section -mt-10"
                  >
                    <div class="order-widget__timer" :class="getTimerUrgencyClass(orderSummary)">
                      <div class="order-widget__timer-content">
                        <span class="order-widget__timer-value">
                          {{ getTimerDisplay(orderSummary) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </swiper-slide>
      </swiper-container>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { consola } from 'consola'
import { useRetryPayment } from '@features/payment'
import { useAuthStore } from '~/features/auth'
import { useOrderSwiper } from '~/widgets/order-modal/@x/current-order-status'
import { useOrderTimers } from '~/widgets/order-modal/@x/current-order-status'
import { useOrderDisplay } from '~/widgets/order-modal/@x/current-order-status'

const retryPayment = useRetryPayment()

const {
  selectOrder,
  openOrderDetailsModal,
  unpaidOrdersStore,
} = retryPayment

// Navigation orders
const navigationOrders = computed(() => unpaidOrdersStore.summaries || [])
const currentOrderId = computed(() => unpaidOrdersStore.selectedOrder?.id)

// Initialize Swiper management
const swiper = useOrderSwiper(navigationOrders, currentOrderId)

const {
  swiperContainerRef,
  swiperKey,
  swiperConfig,
  swiperBreakpoints,
  swiperClasses,
  hasMultipleOrders,
  syncSwiperToOrder,
  updateSwiperParams,
  refreshSwiperInstance,
  getActiveIndexFromEvent,
  currentSlideIndex,
  isSwiperUpdating,
} = swiper

// Initialize Timer management
const timers = useOrderTimers(navigationOrders)

const {
  shouldShowTimer,
  getTimerDisplay,
  getTimerUrgencyClass,
  initializeTimers,
  cleanupOrphanedTimers,
  cleanupAllTimers,
} = timers

// Initialize Display management
const display = useOrderDisplay({
  unpaidOrdersStore,
  selectOrder,
  openOrderDetailsModal,
  slideToOrder: swiper.slideToOrder,
  currentSlideIndex,
  isSwiperUpdating,
  navigationOrders,
})

const {
  shouldShowOrderBlock,
  getBadgeClass,
  getMessage,
  formatOrderId,
  handleOrderClick,
  handleSlideChange: onSlideChangeHandler,
  ensureOrderDetailsLoaded,
} = display

// Event handlers
async function onSlideChange(event: CustomEvent) {
  const activeIndex = getActiveIndexFromEvent(event)
  if (activeIndex === null) return

  await onSlideChangeHandler(activeIndex)
}

// Lifecycle hooks
onMounted(async () => {
  // Загружаем детали для текущего заказа
  if (currentOrderId.value && !unpaidOrdersStore.hasDetails(String(currentOrderId.value))) {
    await ensureOrderDetailsLoaded(String(currentOrderId.value))
  }

  // Синхронизируем слайдер
  await nextTick()
  if (hasMultipleOrders.value) {
    setTimeout(() => {
      syncSwiperToOrder()
    }, 100)
  }

  // Инициализируем таймеры
  initializeTimers()
})

onUnmounted(() => {
  // Очищаем все таймеры
  cleanupAllTimers()
})

// Watchers

// Следим за изменением ID заказа
watch(
  () => currentOrderId.value,
  async (newId, oldId) => {
    if (!newId || newId === oldId) return

    // Загружаем детали если нужно
    if (!unpaidOrdersStore.hasDetails(String(newId))) {
      await ensureOrderDetailsLoaded(String(newId))
    }

    // Синхронизируем слайдер
    if (!isSwiperUpdating.value) {
      await nextTick()
      syncSwiperToOrder()
    }
  }
)

// Следим за изменением количества заказов
watch(
  () => navigationOrders.value.length,
  async (newCount, oldCount) => {
    const wasSingleOrder = (oldCount ?? 0) <= 1
    const isSingleOrder = newCount <= 1
    const crossedThreshold = wasSingleOrder !== isSingleOrder

    if (crossedThreshold) {
      // Пересоздаем swiper при переходе через порог
      await updateSwiperParams()

      if (newCount > 1) {
        await refreshSwiperInstance(true)
      }
    } else if (newCount > 1) {
      // Просто обновляем если заказов больше одного
      await refreshSwiperInstance(true)
    }
  }
)

// Следим за изменением списка заказов
watch(
  () => navigationOrders.value.map(o => o.id).join(','),
  async () => {
    // Управление таймерами
    initializeTimers()
    cleanupOrphanedTimers()

    // Обновляем swiper
    await refreshSwiperInstance(hasMultipleOrders.value)
  }
)
</script>

<style scoped lang="scss">
.order-widget-container {
  @apply w-full py-8;
}

.order-widget-swiper {
  @apply w-full;

  swiper-slide {
    @apply h-auto;
  }

  &.swiper-disabled {
    pointer-events: none;

    swiper-slide {
      pointer-events: auto;
    }
  }
}

.order-widget-slide {
  @apply h-auto;
}

.order-widget {
  @apply relative bg-gradient-to-br from-gray-200 to-gray-200/50 dark:from-gray-950 dark:to-gray-900/50 rounded-3xl overflow-hidden cursor-pointer;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.05),
    0 10px 15px -3px rgba(0, 0, 0, 0.05);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.06);

  &:hover {
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.08),
      0 20px 25px -5px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
    border-color: rgba(59, 130, 246, 0.3);
  }

  &:active {
    transform: translateY(0) scale(0.995);
    transition-duration: 0.15s;
  }

  @media (max-width: 768px) {
    @apply rounded-2xl;
  }

  &__top {
    @apply flex items-start justify-between p-5 pb-4;

    @media (max-width: 768px) {
      @apply p-4 pb-3;
    }
  }

  &__meta {
    @apply flex flex-col gap-2;
  }

  &__badge {
    @apply inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 w-fit;

    &-dot {
      @apply w-2 h-2 rounded-full animate-pulse;
    }

    &-text {
      @apply font-medium tracking-wide;
    }

    &--pending {
      @apply bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400;
      .order-widget__badge-dot {
        @apply bg-amber-500;
      }
    }

    &--failed {
      @apply bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400;
      .order-widget__badge-dot {
        @apply bg-red-500;
      }
    }

    &--cancelled {
      @apply bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300;
      .order-widget__badge-dot {
        @apply bg-gray-500;
      }
    }

    &--refunded {
      @apply bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400;
      .order-widget__badge-dot {
        @apply bg-cyan-500;
      }
    }

    &--confirmed {
      @apply bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400;
      .order-widget__badge-dot {
        @apply bg-green-500;
      }
    }

    &--preparing {
      @apply bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400;
      .order-widget__badge-dot {
        @apply bg-orange-500;
      }
    }

    &--ready {
      @apply bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400;
      .order-widget__badge-dot {
        @apply bg-blue-500;
      }
    }

    &--delivery {
      @apply bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400;
      .order-widget__badge-dot {
        @apply bg-purple-500;
      }
    }

    &--delivered {
      @apply bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400;
      .order-widget__badge-dot {
        @apply bg-emerald-500;
      }
    }

    &--default {
      @apply bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400;
      .order-widget__badge-dot {
        @apply bg-gray-500;
      }
    }
  }

  &__order-id {
    @apply text-3xl font-bold text-gray-900 dark:text-white tracking-tight m-0;
    font-variant-numeric: tabular-nums;

    @media (max-width: 768px) {
      @apply text-xl;
    }
  }

  &__timer {
    @apply relative flex items-center gap-4 p-4 rounded-2xl transition-all duration-300;
    background: linear-gradient(135deg, rgba(251, 146, 60, 0.08), rgba(251, 146, 60, 0.03));
    border: 1.5px solid rgba(251, 146, 60, 0.2);

    &--urgent {
      background: linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(239, 68, 68, 0.06));
      border-color: rgba(239, 68, 68, 0.3);
      animation: urgentPulse 2s ease-in-out infinite;
    }

    &--critical {
      background: linear-gradient(135deg, rgba(220, 38, 38, 0.15), rgba(220, 38, 38, 0.08));
      border-color: rgba(220, 38, 38, 0.4);
      animation: criticalPulse 1s ease-in-out infinite;
    }

    &--expired {
      background: linear-gradient(135deg, rgba(107, 114, 128, 0.08), rgba(107, 114, 128, 0.03));
      border-color: rgba(107, 114, 128, 0.2);
    }

    @media (max-width: 768px) {
      @apply p-3 gap-3 rounded-xl;
    }
  }

  &__timer-content {
    @apply flex flex-col;
  }

  &__timer-value {
    @apply text-2xl font-bold text-orange-600 dark:text-orange-400 tracking-tight;
    font-variant-numeric: tabular-nums;

    .order-widget__timer--urgent & {
      @apply text-red-600 dark:text-red-400;
    }
    .order-widget__timer--critical & {
      @apply text-red-700 dark:text-red-300;
    }
    .order-widget__timer--expired & {
      @apply text-gray-600 dark:text-gray-400;
    }

    @media (max-width: 768px) {
      @apply text-xl;
    }
  }
}

@keyframes urgentPulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(1.02);
  }
}

@keyframes criticalPulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.03);
    box-shadow: 0 0 0 8px rgba(220, 38, 38, 0);
  }
}

@media (hover: none) and (pointer: coarse) {
  .order-widget:hover {
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .order-widget,
  .order-widget__badge-dot {
    transition: none;
    animation: none;
  }
}

@media (prefers-color-scheme: dark) {
  .order-widget {
    border-color: rgba(255, 255, 255, 0.1);
  }
}
</style>
